import { useState, useEffect, useRef, useCallback } from 'react'
import { supabase } from '../services/supabase'
import { useCurrentAdvisor } from './useCurrentAdvisor'

const HEARTBEAT_MS = 30_000
const OFFLINE_AFTER_MS = 90_000

export function useAdvisorPresence() {
  const { advisor } = useCurrentAdvisor()
  const [allPresence, setAllPresence] = useState([]) // [{advisor_id, status, last_seen_at}]
  const heartbeatRef = useRef(null)

  // Upsert own presence row
  const upsertPresence = useCallback(async (advisorId, status = 'online') => {
    await supabase.from('sfp_advisor_presence').upsert({
      advisor_id: advisorId,
      status,
      last_seen_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }, { onConflict: 'advisor_id' })
  }, [])

  const setStandby = useCallback(async (isStandby) => {
    if (!advisor?.id) return
    await upsertPresence(advisor.id, isStandby ? 'standby' : 'online')
  }, [advisor?.id, upsertPresence])

  useEffect(() => {
    if (!advisor?.id) return

    // Fetch all presence rows
    supabase.from('sfp_advisor_presence').select('*').then(({ data }) => {
      setAllPresence(data ?? [])
    })

    // Heartbeat: upsert every 30s
    upsertPresence(advisor.id, 'online')
    heartbeatRef.current = setInterval(() => {
      upsertPresence(advisor.id, 'online')
    }, HEARTBEAT_MS)

    // On tab blur — mark last_seen_at immediately
    // On blur: stamp last_seen_at so the 90s offline timer starts from the moment the user leaves
    const onBlur = () => upsertPresence(advisor.id, 'online')
    window.addEventListener('blur', onBlur)

    // Realtime presence updates
    const ch = supabase
      .channel('advisor-presence-all')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'sfp_advisor_presence' }, (payload) => {
        setAllPresence(prev => {
          const idx = prev.findIndex(p => p.advisor_id === payload.new?.advisor_id)
          if (payload.eventType === 'DELETE') return prev.filter(p => p.advisor_id !== payload.old?.advisor_id)
          if (idx >= 0) { const next = [...prev]; next[idx] = payload.new; return next }
          return [...prev, payload.new]
        })
      })
      .subscribe()

    return () => {
      clearInterval(heartbeatRef.current)
      window.removeEventListener('blur', onBlur)
      supabase.removeChannel(ch)
    }
  }, [advisor?.id, upsertPresence])

  // Derive display status for any advisor_id
  const getStatus = useCallback((advisorId) => {
    const row = allPresence.find(p => p.advisor_id === advisorId)
    if (!row) return 'offline'
    if (row.status === 'standby') return 'standby'
    const age = Date.now() - new Date(row.last_seen_at).getTime()
    return age < OFFLINE_AFTER_MS ? 'online' : 'offline'
  }, [allPresence])

  const getLastSeen = useCallback((advisorId) => {
    const row = allPresence.find(p => p.advisor_id === advisorId)
    return row?.last_seen_at ?? null
  }, [allPresence])

  const myStatus = advisor?.id ? getStatus(advisor.id) : 'offline'

  return { getStatus, getLastSeen, setStandby, myStatus }
}
